/**
 * EL DÍA LÓGICO — donde más apps de hábitos fallan, y donde el usuario lo nota
 * al instante (PRD 7).
 *
 * El principio entero cabe en una frase: **el día es una fecha flotante, no un
 * instante**. `localDate` se calcula UNA VEZ en el cliente al escribir y no se
 * recalcula jamás en el servidor a partir de un timestamp UTC. Si marcaste
 * «leer» el 9 de septiembre, sigue siendo el 9 de septiembre aunque vueles a
 * Tokio.
 *
 * Este fichero no importa nada. Corre igual en el navegador y en Node, y esa es
 * la razón de que viva en `domain` y no en la API.
 */

/** Una fecha civil sin zona: `YYYY-MM-DD`. Lo que Postgres guarda como `DATE`. */
export type LocalDate = string;

/** Zona IANA: `Europe/Madrid`, `America/Bogota`. */
export type TimeZone = string;

/**
 * «Mi día empieza a las…», en minutos desde medianoche.
 * De 0 a 11 h hacia adelante. El tope de 11 h no es estético: garantiza que
 * `localDate` nunca pueda saltar dos días.
 */
export const DAY_START_MIN = 0;
export const DAY_START_MAX = 11 * 60;
export const DAY_START_DEFAULT = 4 * 60;

/** Margen que el servidor acepta al validar una fecha local (PRD 7.1). */
export const LOCAL_DATE_TOLERANCE_DAYS = 2;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formateadores cacheados. `Intl.DateTimeFormat` es caro de construir y esto se
 * llama en bucle al calcular rachas sobre cientos de filas.
 */
const formatters = new Map<TimeZone, Intl.DateTimeFormat>();

function formatterFor(tz: TimeZone): Intl.DateTimeFormat {
  let formatter = formatters.get(tz);
  if (!formatter) {
    // `en-CA` da exactamente YYYY-MM-DD. No es una curiosidad: es la única
    // configuración regional que lo hace sin montar la cadena a mano.
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    formatters.set(tz, formatter);
  }
  return formatter;
}

/**
 * La fecha civil en esa zona, sin desfase de día lógico.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PROHIBIDO, y hay una regla de lint que lo impide:                         │
 * │                                                                          │
 * │   new Date().toISOString().slice(0, 10)                                  │
 * │                                                                          │
 * │ Eso es UTC, no la fecha del usuario. A las 23:50 en Madrid devuelve el    │
 * │ día SIGUIENTE: es literalmente el caso que le rompe la racha a las once   │
 * │ y media de la noche.                                                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function civilDate(instant: Date, tz: TimeZone): LocalDate {
  return formatterFor(tz).format(instant);
}

/** Fecha y hora de pared en la zona. Cacheado por lo mismo que `formatterFor`. */
const wallFormatters = new Map<TimeZone, Intl.DateTimeFormat>();

type Wall = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
};

function wallClock(instant: Date, tz: TimeZone): Wall {
  let formatter = wallFormatters.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    wallFormatters.set(tz, formatter);
  }
  const parts: Record<string, number> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return {
    year: parts['year'] ?? 1970,
    month: parts['month'] ?? 1,
    day: parts['day'] ?? 1,
    // Algún motor antiguo pinta la medianoche como «24» aun con `h23`.
    hour: (parts['hour'] ?? 0) % 24,
    minute: parts['minute'] ?? 0,
    second: parts['second'] ?? 0,
  };
}

/** Desfase de la zona respecto a UTC en ese instante, en ms (Madrid en verano: +2 h). */
function zoneOffsetMs(instant: Date, tz: TimeZone): number {
  const w = wallClock(instant, tz);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const truncated = instant.getTime() - (((instant.getTime() % 1000) + 1000) % 1000);
  return asUtc - truncated;
}

/**
 * La fecha lógica: la civil, o la anterior si en la pared todavía no son las
 * «mi día empieza a las…».
 *
 *   23:50 del día 9  → "2026-09-09"
 *   00:10 del día 10 → "2026-09-09"   ← sigue siendo «ayer», que es lo correcto
 *
 * El modelo mental de «me acosté a las dos, todavía es mi martes» es universal.
 *
 * Se compara la HORA DE PARED con el desfase, en vez de restar el desfase al
 * instante: restar 4 h de reloj el día del cambio de hora hacía que el día
 * empezara a las 3 o a las 5 de la pared. El día empieza a las 4 que marca el
 * reloj del usuario, también ese domingo.
 */
export function logicalDate(
  instant: Date,
  tz: TimeZone,
  dayStartMin: number = DAY_START_DEFAULT,
): LocalDate {
  const offset = clampDayStart(dayStartMin);
  const civil = civilDate(instant, tz);
  if (offset === 0) return civil;
  const wall = wallClock(instant, tz);
  return wall.hour * 60 + wall.minute < offset ? addDays(civil, -1) : civil;
}

/**
 * El instante en que la pared de esa zona marca `minutes` minutos del día civil
 * `date`. Si esa hora no existe —el hueco del cambio de hora de primavera—, cae
 * en la primera hora que sí existe después, que es cuando el reloj la alcanza.
 */
export function zonedInstant(date: LocalDate, minutes: number, tz: TimeZone): Date {
  const wall = Date.parse(`${date}T00:00:00Z`) + minutes * 60_000;
  // Dos pasadas: la primera estima el desfase con la hora de pared como si fuera
  // UTC; la segunda lo corrige con el desfase real del instante estimado.
  const first = wall - zoneOffsetMs(new Date(wall), tz);
  return new Date(wall - zoneOffsetMs(new Date(first), tz));
}

/**
 * El instante en que empieza el día lógico siguiente al de `instant`. Es lo que
 * usa la app para volver a pintar «hoy» y para relevar los recordatorios sin
 * tener que mirar el reloj cada minuto.
 */
export function nextDayStart(
  instant: Date,
  tz: TimeZone,
  dayStartMin: number = DAY_START_DEFAULT,
): Date {
  const offset = clampDayStart(dayStartMin);
  const today = logicalDate(instant, tz, offset);
  const start = zonedInstant(addDays(today, 1), offset, tz);
  // Red de seguridad para zonas raras: nunca en el pasado.
  return start.getTime() > instant.getTime() ? start : new Date(instant.getTime() + 60_000);
}

/** Recorta el desfase al rango permitido en lugar de fallar: nunca bloquea una escritura. */
export function clampDayStart(minutes: number): number {
  if (!Number.isFinite(minutes)) return DAY_START_DEFAULT;
  return Math.min(DAY_START_MAX, Math.max(DAY_START_MIN, Math.round(minutes)));
}

export function isLocalDate(value: unknown): value is LocalDate {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  // Atrapa el 2026-02-30, que pasa el regex y no existe.
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}

/** Días entre dos fechas civiles. Positivo si `b` es posterior. */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const shifted = new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000);
  // Por UTC a propósito: aquí se hace aritmética sobre una fecha civil ya fijada,
  // no se está averiguando en qué día vive nadie. Y aun así va por `civilDate`,
  // para que en todo el repositorio haya UN solo camino de fecha a texto.
  return civilDate(shifted, 'UTC');
}

/**
 * Lo ÚNICO que el servidor valida sobre una fecha local: que sea plausible
 * (PRD 7.1). No la recalcula — no puede: no sabe en qué zona estaba el
 * dispositivo ni a qué hora empieza el día de ese usuario.
 *
 * ±2 días cubre cualquier combinación de zona y desfase (máximo real: ~26 h)
 * con margen de sobra, y sigue atrapando un reloj roto.
 */
export function isPlausibleLocalDate(
  localDate: LocalDate,
  serverNow: Date,
  toleranceDays: number = LOCAL_DATE_TOLERANCE_DAYS,
): boolean {
  if (!isLocalDate(localDate)) return false;
  const serverDate = civilDate(serverNow, 'UTC');
  return Math.abs(daysBetween(serverDate, localDate)) <= toleranceDays;
}

// ---------------------------------------------------------------------------
// RACHAS
// ---------------------------------------------------------------------------

export type StreakDay = {
  localDate: LocalDate;
  /** Zona en la que se escribió. Se guarda con cada entrada justamente para esto. */
  tz: TimeZone;
};

/**
 * La racha se CALCULA, nunca se almacena (PRD 6.11): un contador replicado es la
 * peor estructura posible bajo sincronización —dos dispositivos lo incrementan
 * offline y se pierde uno—. Sobre unos cientos de filas esto es trivial, y
 * elimina el conflicto de raíz.
 *
 * LA REGLA QUE EVITA LA RESEÑA DE UNA ESTRELLA (PRD 7.4):
 * **no se puede romper una racha por un día que el usuario nunca vivió.** Al
 * volar de Bogotá a Madrid se pierden horas y puede aparecer un hueco de un día
 * que nadie se saltó. Si la zona cambió entre dos entradas contiguas, ese hueco
 * de un día se perdona.
 *
 * `days` puede venir en cualquier orden y con repetidos: se normaliza aquí.
 */
export function computeStreak(days: readonly StreakDay[], today: LocalDate): number {
  if (days.length === 0) return 0;

  const byDate = new Map<LocalDate, StreakDay>();
  for (const day of days) {
    if (isLocalDate(day.localDate)) byDate.set(day.localDate, day);
  }

  const ordered = [...byDate.values()].sort((a, b) => (a.localDate < b.localDate ? 1 : -1));
  const newest = ordered[0];
  if (!newest) return 0;

  // Una racha viva es la de hoy o la de ayer. Más atrás, ya se rompió.
  const sinceNewest = daysBetween(newest.localDate, today);
  if (sinceNewest < 0 || sinceNewest > 1) return 0;

  let streak = 1;
  for (let i = 1; i < ordered.length; i += 1) {
    const previous = ordered[i - 1];
    const current = ordered[i];
    if (!previous || !current) break;

    const gap = daysBetween(current.localDate, previous.localDate);
    if (gap === 1) {
      streak += 1;
      continue;
    }
    // El perdón por viaje: exactamente un día, y solo si cambió la zona.
    if (gap === 2 && current.tz !== previous.tz) {
      streak += 1;
      continue;
    }
    break;
  }

  return streak;
}

/**
 * Minutos de reloj local (0–1439) de un instante en una zona. Sirve para decidir
 * cosas que dependen de la hora y no del día, como hasta cuándo se destaca el ritual.
 */
export function localMinutes(instant: Date, tz: TimeZone): number {
  const partes = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const hora = Number(partes.find((p) => p.type === 'hour')?.value ?? 0);
  const minuto = Number(partes.find((p) => p.type === 'minute')?.value ?? 0);
  return (hora % 24) * 60 + minuto;
}
